import { useState, useMemo } from 'react';
import {
  Box, Typography, Button, Card, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert, CircularProgress,
  InputAdornment, Tabs, Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  usePriceGroups, useCreatePriceGroup, useUpdatePriceGroup, useDeletePriceGroup,
  usePriceGroup, useAddPriceGroupCustomers, useRemovePriceGroupCustomer,
} from '../../hooks/useApi';
import type { PriceGroup, PriceGroupInput } from '../../types';

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 2 }).format(n);
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export default function PriceGroupsPage() {
  const { data: groups = [], isLoading } = usePriceGroups();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PriceGroup | null>(null);
  const [detailGroupId, setDetailGroupId] = useState<string | null>(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Prisgrupper</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setEditingGroup(null); setEditorOpen(true); }}>
          Ny prisgruppe
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
      ) : groups.length === 0 ? (
        <Alert severity="info">Ingen prisgrupper endnu. Opret en for at gruppere kunder med fælles priser.</Alert>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Navn</TableCell>
                <TableCell align="right">Antal kunder</TableCell>
                <TableCell align="right">Antal priser</TableCell>
                <TableCell>Rabat</TableCell>
                <TableCell>Gyldig</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(g => (
                <TableRow key={g.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailGroupId(g.id)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{g.name}</Typography>
                    {g.description && <Typography variant="caption" color="text.secondary">{g.description}</Typography>}
                  </TableCell>
                  <TableCell align="right">{g.customerNumbers.length}</TableCell>
                  <TableCell align="right">{g._count?.prices ?? 0}</TableCell>
                  <TableCell>{g.discountPercent != null ? `${g.discountPercent}%` : '—'}</TableCell>
                  <TableCell>
                    {(g.validFrom || g.validUntil) ? (
                      <Typography variant="caption">
                        {fmtDate(g.validFrom)} – {fmtDate(g.validUntil)}
                      </Typography>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => { setEditingGroup(g); setEditorOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {editorOpen && (
        <PriceGroupEditor
          group={editingGroup}
          onClose={() => setEditorOpen(false)}
        />
      )}
      {detailGroupId && (
        <PriceGroupDetail
          groupId={detailGroupId}
          onClose={() => setDetailGroupId(null)}
        />
      )}
    </Box>
  );
}

function PriceGroupEditor({ group, onClose }: { group: PriceGroup | null; onClose: () => void }) {
  const create = useCreatePriceGroup();
  const update = useUpdatePriceGroup();
  const del = useDeletePriceGroup();
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [discountPercent, setDiscountPercent] = useState(group?.discountPercent != null ? String(group.discountPercent) : '');
  const [validFrom, setValidFrom] = useState(group?.validFrom?.slice(0, 10) || '');
  const [validUntil, setValidUntil] = useState(group?.validUntil?.slice(0, 10) || '');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Navn er påkrævet'); return; }
    const payload: PriceGroupInput = {
      name: name.trim(),
      description: description.trim() || null,
      discountPercent: discountPercent.trim() ? parseFloat(discountPercent) : null,
      validFrom: validFrom || null,
      validUntil: validUntil || null,
    };
    try {
      if (group) await update.mutateAsync({ id: group.id, input: payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fejl ved gem');
    }
  };

  const handleDelete = async () => {
    if (!group) return;
    if (!confirm(`Slet prisgruppen "${group.name}"? Alle priser i gruppen slettes også.`)) return;
    try {
      await del.mutateAsync(group.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fejl ved sletning');
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{group ? `Rediger ${group.name}` : 'Ny prisgruppe'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Navn" size="small" value={name} onChange={e => setName(e.target.value)} required />
          <TextField label="Beskrivelse" size="small" value={description} onChange={e => setDescription(e.target.value)} multiline rows={2} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Rabat" size="small" type="number"
              value={discountPercent}
              onChange={e => setDiscountPercent(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              helperText="Valgfri — fratrækkes på alle gruppens priser"
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Gyldig fra" size="small" type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            <TextField label="Gyldig til" size="small" type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {group && (
          <Button color="error" onClick={handleDelete} startIcon={<DeleteIcon />} sx={{ mr: 'auto' }}>
            Slet
          </Button>
        )}
        <Button onClick={onClose}>Annuller</Button>
        <Button variant="contained" onClick={handleSave} disabled={create.isPending || update.isPending}>
          Gem
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PriceGroupDetail({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const { data: group, isLoading } = usePriceGroup(groupId);
  const [tab, setTab] = useState<0 | 1>(0);
  const [searchPrice, setSearchPrice] = useState('');
  const [newCustomers, setNewCustomers] = useState('');
  const addCustomers = useAddPriceGroupCustomers();
  const removeCustomer = useRemovePriceGroupCustomer();

  const filteredPrices = useMemo(() => {
    if (!group?.prices) return [];
    const q = searchPrice.trim().toLowerCase();
    if (!q) return group.prices;
    return group.prices.filter(p =>
      p.salesLocationProduct?.doraProductName?.toLowerCase().includes(q) ||
      p.salesLocationProduct?.salesLocation?.name?.toLowerCase().includes(q)
    );
  }, [group?.prices, searchPrice]);

  if (isLoading || !group) {
    return (
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent><Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      </Dialog>
    );
  }

  const handleAddCustomers = async () => {
    const nums = newCustomers.split(/[,\s\n]+/).map(s => s.trim()).filter(Boolean);
    if (nums.length === 0) return;
    await addCustomers.mutateAsync({ groupId, customerNumbers: nums });
    setNewCustomers('');
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {group.name}
        {group.discountPercent != null && <Chip size="small" label={`Rabat ${group.discountPercent}%`} sx={{ ml: 1 }} />}
        {group.validUntil && <Chip size="small" label={`Til ${fmtDate(group.validUntil)}`} sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => setTab(v as 0 | 1)} sx={{ mb: 2 }}>
          <Tab label={`Priser (${group.prices?.length || 0})`} />
          <Tab label={`Kunder (${group.customerNumbers.length})`} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <TextField
              size="small" placeholder="Søg vare eller lokation..." fullWidth
              value={searchPrice} onChange={e => setSearchPrice(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Box sx={{ maxHeight: 500, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Vare</TableCell>
                    <TableCell>Lokation</TableCell>
                    <TableCell align="right">Pris</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrices.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.salesLocationProduct?.doraProductName || '—'}</TableCell>
                      <TableCell>{p.salesLocationProduct?.salesLocation?.name || '—'}</TableCell>
                      <TableCell align="right">{fmtMoney(p.price)}</TableCell>
                    </TableRow>
                  ))}
                  {filteredPrices.length === 0 && (
                    <TableRow><TableCell colSpan={3} align="center"><Typography variant="caption" color="text.secondary">Ingen priser</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small" placeholder="Tilføj kundenumre (kommasepareret)" fullWidth
                value={newCustomers} onChange={e => setNewCustomers(e.target.value)}
              />
              <Button onClick={handleAddCustomers} variant="contained" disabled={!newCustomers.trim()}>Tilføj</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 400, overflow: 'auto' }}>
              {group.customerNumbers.map(num => (
                <Chip
                  key={num}
                  label={num}
                  size="small"
                  onDelete={async () => { await removeCustomer.mutateAsync({ groupId, customerNumber: num }); }}
                />
              ))}
              {group.customerNumbers.length === 0 && (
                <Typography variant="caption" color="text.secondary">Ingen kunder tilknyttet</Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
      </DialogActions>
    </Dialog>
  );
}
